import React, { useEffect, useMemo, useState } from 'react';
import {
    ComposedChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Scatter,
    Customized,
    usePlotArea,
} from 'recharts';

// Cores tab10 do matplotlib (ordem oficial do colormap discreto).
const TAB10 = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];

/**
 * Replica plt.cm.tab10(np.linspace(0, 1, n)) — amostragem uniforme do colormap
 * (interpolação linear por índice contínuo). Para n=1 retorna o primeiro;
 * para n>=2, distribui pelos índices 0..9.
 */
const amostrarTab10 = (n) => {
    if (n <= 0) return [];
    if (n === 1) return [TAB10[0]];
    const out = [];
    for (let i = 0; i < n; i++) {
        const idx = Math.round((i / (n - 1)) * (TAB10.length - 1));
        out.push(TAB10[idx]);
    }
    return out;
};

const CircleDot = ({ cx, cy, fill, offsetX = 0 }) => {
    if (cx == null || cy == null) return null;
    return <circle cx={cx + offsetX} cy={cy} r={5} fill={fill} stroke={fill} strokeWidth={1.5} />;
};

const desenharCruz = (x, y, cor, tamanho = 6) => (
    <g stroke={cor} strokeWidth={2}>
        <line x1={x - tamanho} y1={y - tamanho} x2={x + tamanho} y2={y + tamanho} />
        <line x1={x + tamanho} y1={y - tamanho} x2={x - tamanho} y2={y + tamanho} />
        <line x1={x} y1={y - tamanho - 3} x2={x} y2={y + tamanho + 3} />
    </g>
);

const desenharTraco = (x, y, cor, tamanho = 6) => (
    <line x1={x - tamanho} y1={y} x2={x + tamanho} y2={y} stroke={cor} strokeWidth={2} />
);

/**
 * Cruz no P50 + traços nos P25 e P75, ligados por linha vertical.
 * Recebe o nome da série para extrair p25/p75 do payload e a função yToPx
 * (valor → coordenada y) para posicionar os traços dos extremos.
 */
const CrossDot = ({ cx, cy, fill, offsetX = 0, payload, serieNome, yToPx }) => {
    if (cx == null || cy == null) return null;
    const x = cx + offsetX;
    const p25 = payload?.[`p25__${serieNome}`];
    const p75 = payload?.[`p75__${serieNome}`];
    const yP25 = p25 != null && yToPx ? yToPx(p25) : null;
    const yP75 = p75 != null && yToPx ? yToPx(p75) : null;
    return (
        <g>
            {yP25 != null && yP75 != null && (
                <line x1={x} y1={yP25} x2={x} y2={yP75} stroke={fill} strokeWidth={2} />
            )}
            {yP25 != null && desenharTraco(x, yP25, fill)}
            {yP75 != null && desenharTraco(x, yP75, fill)}
            {desenharCruz(x, cy, fill)}
        </g>
    );
};

const isPercentual = (metrica) => metrica === 'Desvio percentual' || metrica === 'Prob previsão' || metrica === 'Comparativo realizado';

const formatarValor = (v, metrica) => {
    if (v == null || Number.isNaN(v)) return '—';
    if (isPercentual(metrica)) return `${(v * 100).toFixed(1)}%`;
    return v.toFixed(2);
};

/**
 * Transforma o dicionário { chave: { quantidade, media, p25, p50, p75 } } em array de objetos ordenados.
 */
const serieSimples = (estats) => {
    if (!estats) return [];
    return Object.keys(estats).map((chave) => {
        const e = estats[chave] || {};
        const p25 = e.p25 != null ? Number(e.p25) : null;
        const p50 = e.p50 != null ? Number(e.p50) : null;
        const p75 = e.p75 != null ? Number(e.p75) : null;
        const errInf = p25 != null && p50 != null ? p50 - p25 : null;
        const errSup = p50 != null && p75 != null ? p75 - p50 : null;
        return {
            chave,
            quantidade: e.quantidade != null ? Number(e.quantidade) : 0,
            media: e.media != null ? Number(e.media) : null,
            p50,
            p25,
            p75,
            percentisErro: errInf != null && errSup != null ? [errInf, errSup] : null,
        };
    });
};

const CustomTooltip = ({ active, payload, label, metrica, series }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
        <div style={{ backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: 6, padding: '10px 14px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', minWidth: 180 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#111' }}>{label}</div>
            {series.map(({ nome, cor, dados }) => {
                const d = dados.find(r => r.chave === label);
                if (!d) return null;
                return (
                    <div key={nome} style={{ marginBottom: 4 }}>
                        {series.length > 1 && (
                            <div style={{ color: cor, fontWeight: 600 }}>{nome}</div>
                        )}
                        <div style={{ color: '#374151' }}>Quantidade: <strong>{d.quantidade}</strong></div>
                        {d.media != null && <div style={{ color: '#374151' }}>Média: <strong>{formatarValor(d.media, metrica)}</strong></div>}
                        {d.p50 != null && <div style={{ color: '#374151' }}>P25/P50/P75: {formatarValor(d.p25, metrica)} / {formatarValor(d.p50, metrica)} / {formatarValor(d.p75, metrica)}</div>}
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Gráfico de resumo.
 *
 * Props:
 *  - titulo (string)
 *  - metrica (string) — nome da métrica (para formatação de tooltip/eixo)
 *  - xLabel (string)
 *  - dados:
 *      - modo simples: { chave: { quantidade, media, p25, p50, p75 } }
 *      - modo com divisor: { valorDivisor: { chave: {...} } }
 *  - divisor (string|null) — nome do divisor quando múltiplas séries
 */
const SummaryChart = ({ titulo, metrica, xLabel = 'Transição', dados, divisor = null }) => {
    const { chartData, series } = useMemo(() => {
        if (!dados) return { chartData: [], series: [] };

        // Caso com divisor: dados é { valor: { chave: stats } }
        if (divisor) {
            const valores = Object.keys(dados);
            const cores = amostrarTab10(valores.length);
            const seriesLocal = valores.map((valor, idx) => ({
                nome: String(valor),
                cor: cores[idx],
                dados: serieSimples(dados[valor]),
            }));

            const chavesSet = new Set();
            seriesLocal.forEach(s => s.dados.forEach(d => chavesSet.add(d.chave)));
            const chaves = Array.from(chavesSet).sort();

            const linhas = chaves.map((chave) => {
                const linha = { chave };
                seriesLocal.forEach(s => {
                    const d = s.dados.find(r => r.chave === chave);
                    linha[`media__${s.nome}`] = d?.media ?? null;
                    linha[`p50__${s.nome}`] = d?.p50 ?? null;
                    linha[`p25__${s.nome}`] = d?.p25 ?? null;
                    linha[`p75__${s.nome}`] = d?.p75 ?? null;
                    linha[`percentisErro__${s.nome}`] = d?.percentisErro ?? null;
                    linha[`quantidade__${s.nome}`] = d?.quantidade ?? 0;
                });
                return linha;
            });

            return { chartData: linhas, series: seriesLocal };
        }

        // Modo simples
        const dadosSerie = serieSimples(dados);
        const linhas = dadosSerie.map(d => ({
            chave: d.chave,
            media__serie: d.media,
            p50__serie: d.p50,
            p25__serie: d.p25,
            p75__serie: d.p75,
            percentisErro__serie: d.percentisErro,
            quantidade__serie: d.quantidade,
        }));
        return {
            chartData: linhas,
            series: [{ nome: 'serie', cor: TAB10[0], dados: dadosSerie }],
        };
    }, [dados, divisor]);

    // Para deslocar marcadores em multi-série e desenhar error bars manuais,
    // capturamos a área do plot via Customized + usePlotArea.
    const [plotBox, setPlotBox] = useState({ width: 0, top: 0, height: 0 });

    if (chartData.length === 0) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">{titulo}</h4>
                <p className="text-gray-500 text-sm">Sem dados para exibição.</p>
            </div>
        );
    }

    // Escala Y esquerda: baseada em média/p25/p50/p75 (valores da métrica).
    // Inclui P25 e P75 explicitamente para garantir que cruzes não saiam do plot.
    const valoresY = chartData.flatMap(r => {
        const vs = [];
        series.forEach(s => {
            ['media', 'p25', 'p50', 'p75'].forEach(campo => {
                const v = r[`${campo}__${s.nome}`];
                if (v != null && !Number.isNaN(v)) vs.push(v);
            });
        });
        return vs;
    });

    let yDomain = ['auto', 'auto'];
    if (valoresY.length > 0) {
        const vMin = Math.min(...valoresY);
        const vMax = Math.max(...valoresY);
        const padding = Math.max(Math.abs(vMax - vMin) * 0.15, 0.05);
        if (metrica === 'Desvio percentual') {
            // Usa o domínio fixo do legado, mas amplia se algum valor extrapolar.
            const limite = series.length > 1 ? 0.75 : 0.25;
            yDomain = [
                Math.min(-limite, vMin - padding),
                Math.max(limite, vMax + padding),
            ];
        } else {
            yDomain = [vMin < 0 ? vMin - padding : 0, vMax + padding];
        }
    } else if (metrica === 'Desvio percentual') {
        yDomain = series.length > 1 ? [-0.75, 0.75] : [-0.25, 0.25];
    }

    const yUnidade = metrica === 'Prob previsão' || metrica === 'Comparativo realizado' || metrica === 'Desvio percentual'
        ? ''
        : '';

    // Quando o usuário pediu separação por divisor (ex: Categoria), tratamos
    // como multi-série mesmo com 1 valor único — a legenda fica "<divisor>: <valor>".
    const multiSerie = !!divisor;
    const corRefSimples = TAB10[0];

    const CapturarPlotArea = () => {
        const plotArea = usePlotArea();
        const w = plotArea?.width ?? 0;
        const t = plotArea?.y ?? 0;
        const h = plotArea?.height ?? 0;
        useEffect(() => {
            if (
                Math.abs(w - plotBox.width) > 1 ||
                Math.abs(t - plotBox.top) > 1 ||
                Math.abs(h - plotBox.height) > 1
            ) setPlotBox({ width: w, top: t, height: h });
        }, [w, t, h]);
        return null;
    };
    const plotWidth = plotBox.width;

    // Offset por série (px). Replica o np.linspace(0,1, n) * largura_serie do legado:
    // dentro de cada slot do eixo, distribui n marcadores uniformemente.
    // Em multi-série, o Recharts agrupa as Bars dentro de cada slot deixando
    // 10% de gap de cada lado (default). Replicamos isso para os Scatters.
    const calcularOffset = (idxSerie) => {
        if (!multiSerie) return 0;
        const n = series.length;
        const slot = chartData.length > 0 ? plotWidth / chartData.length : 0;
        const larguraBarras = slot * 0.9; // 10% gap nas laterais
        const larguraBarra = larguraBarras / n;
        // Centro da barra i dentro do slot, relativo ao centro do slot.
        return -larguraBarras / 2 + larguraBarra / 2 + idxSerie * larguraBarra;
    };

    // Converter valor da métrica (eixo Y esquerdo) em coordenada de pixel.
    const yToPx = (valor) => {
        if (plotBox.height <= 0) return null;
        const [yMin, yMax] = yDomain;
        if (typeof yMin !== 'number' || typeof yMax !== 'number') return null;
        if (yMax === yMin) return plotBox.top + plotBox.height / 2;
        return plotBox.top + (yMax - valor) / (yMax - yMin) * plotBox.height;
    };

    // Legenda desenhada como SVG dentro do chart, replicando o legado:
    // - Multi-série: uma linha por categoria, círculo colorido + "<divisor>: <valor>".
    // - Simples: "Média" (círculo) e "Quartis (P25, P50, P75)" (cruz).
    const itensLegenda = multiSerie
        ? series.map((s) => ({
            tipo: 'circle',
            cor: s.cor,
            texto: divisor ? `${divisor}: ${s.nome}` : s.nome,
        }))
        : [
            { tipo: 'circle', cor: corRefSimples, texto: 'Média' },
            { tipo: 'cross', cor: corRefSimples, texto: 'Quartis (P25, P50, P75)' },
        ];

    const LegendaSobreposta = () => {
        const plotArea = usePlotArea();
        if (!plotArea) return null;
        const { x: left = 0, y: top = 0, width = 0 } = plotArea;
        const padding = 8;
        const linhaH = 20;
        // Largura aproximada por glifo (~7px @12px) + ícone + paddings.
        const maiorTexto = itensLegenda.reduce((acc, it) => Math.max(acc, it.texto.length), 0);
        const larguraBox = Math.max(140, maiorTexto * 7 + 36);
        const x = left + width - larguraBox - 6;
        const y = top + 6;
        const altura = padding * 2 + itensLegenda.length * linhaH;

        return (
            <g>
                <rect
                    x={x}
                    y={y}
                    width={larguraBox}
                    height={altura}
                    fill="rgba(255, 255, 255, 0.85)"
                    stroke="#9ca3af"
                    strokeWidth={0.5}
                    rx={3}
                />
                {itensLegenda.map((it, i) => {
                    const linhaY = y + padding + i * linhaH + 12;
                    const iconX = x + padding;
                    const iconY = linhaY - 6;
                    const textoX = iconX + 20;
                    return (
                        <g key={`i-${i}`}>
                            {it.tipo === 'circle' && (
                                <circle cx={iconX + 7} cy={iconY + 4} r={5} fill={it.cor} />
                            )}
                            {it.tipo === 'cross' && (
                                <g>
                                    <line x1={iconX + 2} y1={iconY - 1} x2={iconX + 12} y2={iconY + 9} stroke={it.cor} strokeWidth={2} />
                                    <line x1={iconX + 12} y1={iconY - 1} x2={iconX + 2} y2={iconY + 9} stroke={it.cor} strokeWidth={2} />
                                    <line x1={iconX + 7} y1={iconY - 4} x2={iconX + 7} y2={iconY + 12} stroke={it.cor} strokeWidth={2} />
                                </g>
                            )}
                            <text x={textoX} y={linhaY + 2} fontSize={12} fill="#111827">
                                {it.texto}
                            </text>
                        </g>
                    );
                })}
            </g>
        );
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">{titulo}</h4>
            <div style={{ position: 'relative' }}>
                <ResponsiveContainer width="100%" height={500}>
                    <ComposedChart data={chartData} margin={{ top: 20, right: 60, left: 20, bottom: 70 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                        <XAxis
                            dataKey="chave"
                            angle={-30}
                            textAnchor="end"
                            interval={0}
                            height={70}
                            tick={{ fontSize: 11, fill: '#374151' }}
                            label={{ value: xLabel, position: 'insideBottom', offset: -10, fontSize: 12, fill: '#374151' }}
                        />
                        <YAxis
                            yAxisId="left"
                            domain={yDomain}
                            tick={{ fontSize: 11, fill: '#374151' }}
                            tickFormatter={(v) => isPercentual(metrica) ? `${(v * 100).toFixed(0)}%` : v}
                            label={{ value: metrica + (yUnidade ? ` (${yUnidade})` : ''), angle: -90, position: 'insideLeft', offset: 10, fontSize: 12, fill: '#374151' }}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 11, fill: '#374151' }}
                            label={{ value: 'Quantidade', angle: 90, position: 'insideRight', offset: 10, fontSize: 12, fill: '#374151' }}
                        />
                        <Tooltip content={<CustomTooltip metrica={metrica} series={series} />} />
                        {series.map((s) => (
                            <Bar
                                key={`bar-${s.nome}`}
                                yAxisId="right"
                                dataKey={`quantidade__${s.nome}`}
                                fill={s.cor}
                                fillOpacity={0.15}
                                legendType="none"
                            />
                        ))}
                        {series.map((s, idx) => (
                            <Scatter
                                key={`media-${s.nome}`}
                                yAxisId="left"
                                dataKey={`media__${s.nome}`}
                                legendType="none"
                                shape={<CircleDot fill={s.cor} offsetX={calcularOffset(idx)} />}
                                isAnimationActive={false}
                            />
                        ))}
                        {series.map((s, idx) => (
                            <Scatter
                                key={`p50-${s.nome}`}
                                yAxisId="left"
                                dataKey={`p50__${s.nome}`}
                                legendType="none"
                                shape={<CrossDot fill={s.cor} offsetX={calcularOffset(idx)} serieNome={s.nome} yToPx={yToPx} />}
                                isAnimationActive={false}
                            />
                        ))}
                        <Customized component={CapturarPlotArea} />
                        <Customized component={LegendaSobreposta} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default SummaryChart;
